import { LogManager } from '@src/utility/Logger';
import { runInAction } from 'mobx';

/**
 * TimelineZoomService
 * Encapsulates zoom & fit operations for the timeline viewport.
 * Stateless: reads current reactive store values at invocation time via providers.
 */
export interface TimelineZoomVideoCreatorLike {
  timelineScale: number;
  maxTimelineScale: number;
  defaultTimelineScale: number;
}

export interface TimelineZoomServiceDeps {
  videoCreator: TimelineZoomVideoCreatorLike;
  getDuration: () => number; // dynamic duration (seconds)
  getMaxWidth: () => number; // current pixel width available
  minTimelineScale?: number; // default 0.1
  paddingPx?: number; // default 30 (matching existing code)
  fitScaleMultiplier?: number; // default 0.95
  zoomInFactor?: number; // default 1.2
  zoomOutFactor?: number; // default 0.8
}

export class TimelineZoomService {
  private readonly _videoCreator: TimelineZoomVideoCreatorLike;
  private readonly _getDuration: () => number;
  private readonly _getMaxWidth: () => number;
  private readonly _min: number;
  private readonly _padding: number;
  private readonly _fitMultiplier: number;
  private readonly _zoomInFactor: number;
  private readonly _zoomOutFactor: number;
  private readonly _logger = LogManager.getLogger(
    'service:TimelineZoomService',
  );

  constructor(deps: TimelineZoomServiceDeps) {
    this._videoCreator = deps.videoCreator;
    this._getDuration = deps.getDuration;
    this._getMaxWidth = deps.getMaxWidth;
    this._min = deps.minTimelineScale ?? 0.1;
    this._padding = deps.paddingPx ?? 30;
    this._fitMultiplier = deps.fitScaleMultiplier ?? 0.95;
    this._zoomInFactor = deps.zoomInFactor ?? 1.2;
    this._zoomOutFactor = deps.zoomOutFactor ?? 0.8;
  }

  /** Increase scale by zoomInFactor (clamped). */
  zoomIn(): void {
    const { timelineScale, maxTimelineScale } = this._videoCreator;
    const next = Math.min(timelineScale * this._zoomInFactor, maxTimelineScale);
    runInAction(() => (this._videoCreator.timelineScale = next));
  }

  /** Decrease scale by zoomOutFactor (clamped to min). */
  zoomOut(): void {
    const { timelineScale } = this._videoCreator;
    const next = Math.max(timelineScale * this._zoomOutFactor, this._min);
    runInAction(() => (this._videoCreator.timelineScale = next));
  }

  /** Compute scale that fits entire duration into available width. */
  computeFitScale(): number | null {
    const duration = this._getDuration();
    const maxWidth = this._getMaxWidth();
    if (!duration || !maxWidth) return null;
    return ((maxWidth - this._padding) / duration) * this._fitMultiplier;
  }

  /** Apply fit scale (if computable). */
  zoomToFit(): void {
    const fit = this.computeFitScale();
    if (fit == null) return;
    runInAction(() => (this._videoCreator.timelineScale = fit));
  }
}

/**
 * Backwards compatibility temporary re-export / alias.
 * Remove usages of TimelineViewService and these aliases, then delete this section.
 */
export type TimelineViewVideoCreatorLike = TimelineZoomVideoCreatorLike; // legacy alias
export type TimelineViewServiceDeps = TimelineZoomServiceDeps; // legacy alias
export const TimelineViewService = TimelineZoomService; // legacy alias
